import React from 'react';

interface ContactTabProps {
  offices: any[];
  getContactValue: (office: any, field: string) => any;
  setContactField: (office: any, field: string, value: any) => void;
  onAdd: () => void;
  onDelete: (office: any) => void;
  onSaveAll: () => void;
  isAdding: boolean;
  isSaving: boolean;
}

const ContactTab: React.FC<ContactTabProps> = ({
  offices, getContactValue, setContactField,
  onAdd, onDelete, onSaveAll, isAdding, isSaving,
}) => (
  <div className="space-y-6">
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
      <h3 className="font-semibold text-blue-900 mb-2">Contact Information</h3>
      <p className="text-sm text-blue-800">Manage office locations and contact details</p>
    </div>

    <div className="flex justify-end mb-4">
      <button onClick={onAdd} disabled={isAdding} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
        {isAdding ? 'Adding...' : '+ Add Office'}
      </button>
    </div>

    <div className="space-y-4">
      {offices.map((office: any) => (
        <div key={office.documentId || office.id} className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex justify-between items-start mb-4">
            <h4 className="text-lg font-semibold text-gray-900">{getContactValue(office, 'name')}</h4>
            <button onClick={() => onDelete(office)} className="text-red-600 hover:text-red-800">🗑️ Remove</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Office Name</label>
              <input type="text" value={getContactValue(office, 'name')} onChange={(e) => setContactField(office, 'name', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Office Type</label>
              <select value={getContactValue(office, 'officeType')} onChange={(e) => setContactField(office, 'officeType', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded">
                <option value="head">Head Office</option>
                <option value="branch">Branch Office</option>
                <option value="representative">Representative Office</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <input type="text" value={getContactValue(office, 'country')} onChange={(e) => setContactField(office, 'country', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input type="text" value={getContactValue(office, 'city')} onChange={(e) => setContactField(office, 'city', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded" />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <input type="text" value={getContactValue(office, 'addressLine1')} onChange={(e) => setContactField(office, 'addressLine1', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={getContactValue(office, 'email')} onChange={(e) => setContactField(office, 'email', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Numbers</label>
              <input
                type="text"
                value={Array.isArray(getContactValue(office, 'phoneNumbers')) ? (getContactValue(office, 'phoneNumbers') as string[]).join(', ') : getContactValue(office, 'phoneNumbers')}
                onChange={(e) => setContactField(office, 'phoneNumbers', e.target.value.split(',').map((p: string) => p.trim()))}
                placeholder="Separate with commas"
                className="w-full px-3 py-2 border border-gray-300 rounded"
              />
            </div>
          </div>

          <div className="mt-4 flex items-center">
            <input type="checkbox" checked={getContactValue(office, 'isActive')} onChange={(e) => setContactField(office, 'isActive', e.target.checked)} className="h-4 w-4 text-blue-600" />
            <label className="ml-2 text-sm text-gray-700">Active</label>
          </div>
        </div>
      ))}
    </div>

    <div className="flex justify-end pt-4 border-t">
      <button onClick={onSaveAll} disabled={isSaving} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
        {isSaving ? 'Saving...' : 'Save Contact Settings'}
      </button>
    </div>
  </div>
);

export default ContactTab;
